import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API } from "../lib/api";
import Header from "../components/Header";
import CircularProgress from "../components/CircularProgress";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Music4, Video, ChevronLeft, CheckCircle2, Loader2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const UPPER_FIELDS = ["cognome", "nome", "telefono", "asa", "tecnico"];

export default function UploadForm() {
  const { id, kind } = useParams(); // kind: 'musica' or 'video'
  const navigate = useNavigate();

  const [comp, setComp] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [classes, setClasses] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [tipologie, setTipologie] = useState([]);

  const [form, setForm] = useState({
    cognome: "",
    nome: "",
    gruppo: "",
    disciplina: "",
    categoria: "",
    classe: "",
    tipologia: "",
    telefono: "",
    asa: "",
    tecnico: "",
    sender_email: "",
  });
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [storing, setStoring] = useState(false);
  const [done, setDone] = useState(null);

  const accent = kind === "musica" ? "#FF5722" : "#00E5FF";
  const Icon = kind === "musica" ? Music4 : Video;
  const accept = kind === "musica" ? ".mp3,audio/mpeg" : ".mp4,video/mp4";

  useEffect(() => {
    Promise.all([
      api.get(`/competitions/${id}`),
      api.get(`/meta/disciplines/${kind}`),
      api.get(`/meta/classes`),
      api.get(`/meta/categorie`),
      api.get(`/meta/tipologie`),
    ])
      .then(([c, d, cl, cat, tip]) => {
        setComp(c.data);
        setDisciplines(d.data);
        setClasses(cl.data);
        setCategorie(cat.data);
        setTipologie(tip.data);
      })
      .catch(() => toast.error("Errore caricamento dati"));
  }, [id, kind]);

  const setField = (name, value) => {
    let v = value;
    if (UPPER_FIELDS.includes(name)) v = (v || "").toUpperCase();
    setForm((f) => ({ ...f, [name]: v }));
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (!f) return setFile(null);
    const ext = f.name.split(".").pop().toLowerCase();
    if (kind === "musica" && ext !== "mp3") {
      toast.error("Solo file MP3 ammessi");
      e.target.value = "";
      return;
    }
    if (kind === "video" && ext !== "mp4") {
      toast.error("Solo file MP4 ammessi");
      e.target.value = "";
      return;
    }
    if (kind === "video" && f.size > 100 * 1024 * 1024) {
      toast.error("Il file Video supera i 100MB");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const validate = () => {
    const required = [
      "cognome", "nome", "disciplina", "categoria",
      "classe", "tipologia", "telefono", "asa", "tecnico",
    ];
    for (const k of required) {
      if (!form[k]) {
        toast.error(`Campo obbligatorio: ${k}`);
        return false;
      }
    }
    if (!file) {
      toast.error("Seleziona il file da caricare");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    data.append("file", file);

    setProgress(0);
    setDone(null);
    try {
      const resp = await axios.post(
        `${API}/competitions/${id}/submit/${kind}`,
        data,
        {
          onUploadProgress: (event) => {
            if (event.total) {
              const pct = Math.round((event.loaded * 100) / event.total);
              setProgress(pct);
              if (pct >= 100) setStoring(true);
            }
          },
        }
      );
      setStoring(false);
      setProgress(null);
      setDone(resp.data);
      toast.success("File memorizzato correttamente!");
    } catch (err) {
      setStoring(false);
      setProgress(null);
      const msg = err.response?.data?.detail || "Errore durante l'invio";
      toast.error(msg);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Header />
        <main className="max-w-2xl mx-auto px-6 py-20">
          <div
            className="bg-[#0E0E11] border border-white/10 rounded-2xl p-10 text-center"
            data-testid="upload-success"
            style={{ borderTop: `4px solid ${accent}` }}
          >
            <CheckCircle2 className="w-16 h-16 mx-auto mb-5" style={{ color: accent }} />
            <h2 className="text-2xl font-bold mb-3">Caricamento completato</h2>
            <p className="text-white/75 leading-relaxed">
              Il tuo file è stato correttamente memorizzato nella cartella di destinazione
              Disciplina-Categoria-Classe-Tipologia come da te indicati per la competizione scelta.
            </p>
            <div className="mt-4 text-xs text-white/40 break-all">
              {done.folder}/{done.filename}
            </div>
            <p className="text-white/60 text-sm mt-6">
              Per assistenza tecnica, contattare il supporto tramite ticket nell'area assistenza in alto.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Button
                onClick={() => navigate(`/competizioni/${id}`)}
                data-testid="back-to-comp-btn"
                className="rounded-full"
                variant="outline"
              >
                Torna alla competizione
              </Button>
              <Button
                onClick={() => {
                  setDone(null);
                  setFile(null);
                  setForm({
                    cognome: "", nome: "", gruppo: "",
                    disciplina: "", categoria: "", classe: "", tipologia: "",
                    telefono: "", asa: "", tecnico: "", sender_email: "",
                  });
                }}
                data-testid="upload-another-btn"
                className="rounded-full text-white border-0"
                style={{
                  background: `linear-gradient(135deg, ${accent} 0%, #00E5FF 100%)`,
                }}
              >
                Invia un altro file
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="text-white/60 hover:text-white text-sm flex items-center gap-1 mb-5"
        >
          <ChevronLeft className="w-4 h-4" /> Indietro
        </button>

        <div className="mb-8 flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ background: `${accent}1A`, color: accent, border: `1px solid ${accent}33` }}
          >
            <Icon className="w-7 h-7" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/40">
              {kind === "musica" ? "Invio Musica" : "Invio Video"}
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">
              {comp ? comp.name : "..."}
            </h1>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[#0E0E11] border border-white/10 rounded-2xl p-6 md:p-8 space-y-5"
          style={{ borderTop: `4px solid ${accent}` }}
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Cognome (ATLETA) *" name="cognome" value={form.cognome} onChange={setField} testId="field-cognome" />
            <Field label="Nome (ATLETA) *" name="nome" value={form.nome} onChange={setField} testId="field-nome" />
            <Field label="Nome Duo o Gruppo (facoltativo)" name="gruppo" value={form.gruppo} onChange={(n, v) => setForm((f) => ({ ...f, gruppo: v }))} testId="field-gruppo" />
            <Field label="Telefono *" name="telefono" value={form.telefono} onChange={setField} testId="field-telefono" />
            <Field label="ASA di Riferimento *" name="asa" value={form.asa} onChange={setField} testId="field-asa" />
            <Field label="Tecnico di Riferimento *" name="tecnico" value={form.tecnico} onChange={setField} testId="field-tecnico" />
            <div className="md:col-span-2">
              <Label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 inline-block">
                Email per riepilogo invio (facoltativa)
              </Label>
              <Input
                data-testid="field-sender-email"
                type="email"
                placeholder="es. tuo.indirizzo@email.it"
                value={form.sender_email}
                onChange={(e) => setForm((f) => ({ ...f, sender_email: e.target.value }))}
                className="bg-black/60 border-white/10 text-white focus-visible:ring-cyan-400 focus-visible:border-cyan-400"
              />
              <div className="text-xs text-white/40 mt-1">
                Se la inserisci, riceverai una email di conferma con il riepilogo dei dati inviati.
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <SelectField
              label="Disciplina *"
              value={form.disciplina}
              onChange={(v) => setForm((f) => ({ ...f, disciplina: v }))}
              options={disciplines}
              placeholder="Scegli disciplina"
              testId="field-disciplina"
            />
            <SelectField
              label="Categoria *"
              value={form.categoria}
              onChange={(v) => setForm((f) => ({ ...f, categoria: v }))}
              options={categorie}
              placeholder="Scegli categoria"
              testId="field-categoria"
            />
            <SelectField
              label="Classe *"
              value={form.classe}
              onChange={(v) => setForm((f) => ({ ...f, classe: v }))}
              options={classes}
              placeholder="Scegli classe"
              testId="field-classe"
            />
            <SelectField
              label="Tipologia *"
              value={form.tipologia}
              onChange={(v) => setForm((f) => ({ ...f, tipologia: v }))}
              options={tipologie}
              placeholder="Scegli tipologia"
              testId="field-tipologia"
            />
          </div>

          <div>
            <Label className="text-sm text-white/70 mb-2 inline-block">
              {kind === "musica" ? "Traccia Musicale (MP3) *" : "Traccia Video (MP4, max 100MB) *"}
            </Label>
            <label
              htmlFor="lmkt-file-input"
              data-testid="file-dropzone"
              className="block cursor-pointer bg-black/60 border-2 border-dashed border-white/15 hover:border-white/30 rounded-xl p-6 text-center transition-colors"
            >
              <UploadCloud className="w-10 h-10 mx-auto text-white/40 mb-2" style={{ color: accent }} />
              <div className="text-sm text-white/70">
                {file ? (
                  <span className="font-medium text-white">{file.name}</span>
                ) : (
                  <>Clicca per selezionare o trascina qui il tuo file {kind === "musica" ? "MP3" : "MP4"}</>
                )}
              </div>
              {file && (
                <div className="text-xs text-white/40 mt-1">
                  {(file.size / (1024 * 1024)).toFixed(1)} MB
                </div>
              )}
              <input
                id="lmkt-file-input"
                data-testid="file-input"
                type="file"
                accept={accept}
                className="hidden"
                onChange={onFileChange}
              />
            </label>
          </div>

          {progress !== null && (
            <div className="bg-black/40 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-5">
                <CircularProgress percent={progress} />
                <div className="flex-1">
                  <div className="text-sm text-white/70 mb-2">
                    {storing ? "Memorizzazione nella cartella di destinazione..." : "Caricamento in corso..."}
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      data-testid="upload-progress-bar"
                      className="h-full transition-all"
                      style={{
                        width: `${progress}%`,
                        background: "linear-gradient(90deg, #FF5722, #00E5FF)",
                      }}
                    />
                  </div>
                  {storing && (
                    <div className="text-xs text-white/40 mt-2 flex items-center gap-1.5">
                      <Loader2 className="w-3 h-3 animate-spin" /> Attendere il completamento
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <Button
            type="submit"
            data-testid="submit-upload-btn"
            disabled={progress !== null}
            className="w-full h-14 rounded-full font-bold tracking-widest text-white border-0 disabled:opacity-50"
            style={{
              background: `linear-gradient(135deg, ${accent} 0%, #00E5FF 100%)`,
              boxShadow: `0 0 30px ${accent}66`,
            }}
          >
            {progress !== null ? "INVIO IN CORSO..." : "INVIA"}
          </Button>
        </form>
      </main>
    </div>
  );
}

function Field({ label, name, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 inline-block">
        {label}
      </Label>
      <Input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className="bg-black/60 border-white/10 text-white focus-visible:ring-cyan-400 focus-visible:border-cyan-400"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder, disabled, testId }) {
  return (
    <div>
      <Label className="text-xs text-white/60 uppercase tracking-wider mb-1.5 inline-block">
        {label}
      </Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger
          data-testid={testId}
          className="bg-black/60 border-white/10 text-white"
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-[#101014] border-white/10 text-white max-h-[300px]">
          {options.map((o) => (
            <SelectItem key={o} value={o} data-testid={`${testId}-opt-${o}`}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
